import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, toggleLanguage } = useLanguage();
  const { totalItems, setIsOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/shop', label: t('nav_shop') },
    { to: '/about', label: t('nav_about') },
    { to: '/rent-gin-bar', label: language === 'sr' ? 'Rentiraj Džin Bar' : 'Rent Gin Bar' },
    { to: '/contact', label: t('nav_contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            data-testid="header-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/9qerwdv2_Logo-za-sajt.svg" 
              alt="Destilerija Kovačić"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm tracking-[0.1em] uppercase transition-colors ${
                  location.pathname === link.to
                    ? 'text-gold'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4 md:gap-6">
            {/* Language Switcher - Always visible */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 md:gap-2 text-xs tracking-[0.15em] text-white/50 hover:text-gold transition-colors uppercase"
              data-testid="language-toggle"
            >
              <span className={language === 'sr' ? 'text-gold' : ''}>SR</span>
              <span className="text-white/30">|</span>
              <span className={language === 'en' ? 'text-gold' : ''}>EN</span>
            </button>

            {/* Cart Button */}
            <button
              onClick={() => setIsOpen(true)}
              className="relative p-2 text-white/80 hover:text-gold transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag size={22} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-black text-xs w-5 h-5 flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0A0A0A] border-t border-white/5 animate-fade-in" data-testid="mobile-menu">
          <nav className="px-6 py-8 flex flex-col gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-lg font-heading transition-colors ${
                  location.pathname === link.to
                    ? 'text-gold'
                    : 'text-white/60'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
